/**
 * threeDBuilder.js  — converts DCEL state to Three.js geometry
 *
 * No external deps beyond Three.js (r128, already on CDN).
 * Called by ThreeDView.jsx when the 3D toggle is active.
 *
 * Produces:
 *   - Wall meshes  (BoxGeometry extruded from wall segments)
 *   - Floor slabs  (PlaneGeometry per detected face)
 *   - Opening voids (approximated: gap in the wall mesh)
 *   - Symbol markers (small cylinders for furniture/plumbing/electrical)
 *
 * Coordinate system:
 *   DCEL uses mm, +X right, +Y down (SVG convention)
 *   Three.js: +X right, +Y up, +Z toward camera
 *   Conversion: three.x = dcel.x/1000, three.z = dcel.y/1000, three.y = height/1000
 */

// ─── Coordinate helpers ───────────────────────────────────────────────────────
const MM = 0.001; // mm → metres

function toV3(x, y, z) {
  // x,y in mm, z in mm height
  return [x * MM, z * MM, y * MM];
}

// ─── Material colours (matching DCEL material IDs) ───────────────────────────
const MAT_COLORS = {
  BRICK:     0x8B6358,
  AAC:       0xC0C0C0,
  HOLLOW:    0xA9A9A9,
  PARTITION: 0xE8E8D0,
  GLASS:     0xB0D0FF,
  RC:        0x969696,
};

const ROOM_COLORS_3D = {
  ROOM:     0x1a2030, BEDROOM:  0x1a2030, LIVING:   0x1c2418,
  KITCHEN:  0x241c18, BATHROOM: 0x181c28, TOILET:   0x181c28,
  STORE:    0x1c1c1c, CORRIDOR: 0x1e1e24, OFFICE:   0x1a2030,
  BALCONY:  0x181e18,
};

// ─── Build scene objects ──────────────────────────────────────────────────────

/**
 * buildWallMeshes(st, THREE)
 * Returns array of THREE.Mesh — one per wall edge.
 */
export function buildWallMeshes(st, THREE) {
  const meshes = [];

  for (const edge of Object.values(st.edges || {})) {
    const ab = st.halfEdges[edge.halfEdgeA];
    const ba = st.halfEdges[edge.halfEdgeB];
    if (!ab || !ba) continue;
    const nA = st.nodes[ab.originId];
    const nB = st.nodes[ba.originId];
    if (!nA || !nB) continue;

    const lenMm = Math.sqrt((nB.x - nA.x) ** 2 + (nB.y - nA.y) ** 2);
    if (lenMm < 50) continue;

    const thickMm  = edge.thickness || 230;
    const heightMm = edge.height    || 3000;

    // Centre of the wall segment in world mm
    const cx = (nA.x + nB.x) / 2;
    const cy = (nA.y + nB.y) / 2;

    const color = MAT_COLORS[edge.materialId || "BRICK"] || 0x888888;

    const geo = new THREE.BoxGeometry(
      lenMm * MM,
      heightMm * MM,
      thickMm * MM
    );
    const mat = new THREE.MeshLambertMaterial({
      color,
      transparent: edge.materialId === "GLASS",
      opacity:     edge.materialId === "GLASS" ? 0.4 : 1,
    });

    const mesh = new THREE.Mesh(geo, mat);

    // Position at wall centre, raised to half height
    mesh.position.set(cx * MM, (heightMm / 2) * MM, cy * MM);

    // Rotate to align with wall direction
    const angle = Math.atan2(nB.y - nA.y, nB.x - nA.x);
    mesh.rotation.y = -angle;

    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData = { type: "wall", edgeId: edge.id };
    meshes.push(mesh);
  }

  return meshes;
}

/**
 * buildFloorSlabs(st, THREE)
 * Returns array of THREE.Mesh — one flat slab per detected face/room.
 */
export function buildFloorSlabs(st, THREE) {
  const meshes = [];

  for (const [faceId, face] of Object.entries(st.faces || {})) {
    if (!face.loop || face.loop.length < 3) continue;

    // Collect ordered polygon points
    const pts = face.loop.map(hid => {
      const he = st.halfEdges[hid];
      if (!he) return null;
      return st.nodes[he.originId];
    }).filter(Boolean);
    if (pts.length < 3) continue;

    // Use THREE.Shape to define the floor polygon
    const shape = new THREE.Shape();
    shape.moveTo(pts[0].x * MM, pts[0].y * MM);
    for (let i = 1; i < pts.length; i++) {
      shape.lineTo(pts[i].x * MM, pts[i].y * MM);
    }
    shape.closePath();

    const geo = new THREE.ShapeGeometry(shape);
    // Rotate flat: ShapeGeometry is in XY plane, we want it in XZ plane
    geo.rotateX(-Math.PI / 2);

    const room = (st.rooms || {})[faceId];
    const roomType = room?.type || "ROOM";
    const color = ROOM_COLORS_3D[roomType] || 0x1a2030;

    const mat = new THREE.MeshLambertMaterial({
      color,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.y = -0.005; // just below floor level (5mm)
    mesh.receiveShadow = true;
    mesh.userData = { type: "floor", faceId };
    meshes.push(mesh);
  }

  return meshes;
}

/**
 * buildSymbolMarkers(st, THREE)
 * Simple cylinders for symbol placement.
 */
export function buildSymbolMarkers(st, THREE) {
  const meshes = [];
  const SYMBOL_COLORS = {
    electrical: 0xfacc15,
    plumbing:   0x22d3ee,
    furniture:  0xa78bfa,
    structural: 0xf97316,
  };

  for (const sym of Object.values(st.symbols || {})) {
    const col = SYMBOL_COLORS[sym.layerId] || 0x6b7e94;
    const geo = new THREE.CylinderGeometry(0.15, 0.15, 0.05, 8);
    const mat = new THREE.MeshLambertMaterial({ color: col });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(sym.x * MM, 0.025, sym.y * MM);
    mesh.userData = { type: "symbol", symId: sym.id };
    meshes.push(mesh);
  }

  return meshes;
}

/**
 * buildScene(st, THREE)
 * Convenience: returns { walls, floors, symbols } mesh arrays.
 */
export function buildScene(st, THREE) {
  return {
    walls:   buildWallMeshes(st,    THREE),
    floors:  buildFloorSlabs(st,    THREE),
    symbols: buildSymbolMarkers(st, THREE),
  };
}

/**
 * getBoundingBox(st)
 * Returns { cx, cy, size } in metres — used to position the camera.
 */
export function getBoundingBox(st) {
  const nodes = Object.values(st.nodes || {});
  if (!nodes.length) return { cx: 0, cy: 0, size: 10 };
  const xs = nodes.map(n => n.x), ys = nodes.map(n => n.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  return {
    cx:   ((minX + maxX) / 2) * MM,
    cy:   ((minY + maxY) / 2) * MM,
    size: Math.max((maxX - minX), (maxY - minY)) * MM * 1.5 || 10,
  };
}
